import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiSearch } from 'react-icons/fi';
import { ACTION_META, fmtDateTime, fieldLabel, roleLabel } from './activityMeta';

const show = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string' && (v.startsWith('[') || v.startsWith('{'))) {
    try { return JSON.stringify(JSON.parse(v)); } catch (e) { return v; }
  }
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleDateString('en-GB', { timeZone: 'Asia/Riyadh' });
  return String(v);
};

export const ChangeList = ({ changes }) => (
  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px', marginTop: 6, fontSize: 12 }}>
    {changes.map((c, i) => (
      <div key={i} style={{ padding: '2px 0' }}>
        <strong>{fieldLabel(c.field)}:</strong>{' '}
        <span style={{ color: '#ef4444', textDecoration: 'line-through' }}>{show(c.from)}</span>
        {' → '}
        <span style={{ color: '#059669', fontWeight: 600 }}>{show(c.to)}</span>
      </div>
    ))}
  </div>
);

// Full statement / audit trail: every login, customer, order, bill and payment
// action across all shops — who did it, when, from which device, and (for edits)
// exactly what changed.
export default function SuperAdminActivity() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [action, setAction] = useState(sp.get('action') || '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [open, setOpen] = useState(null);

  useEffect(() => { superAdminApi.get('/shops').then(r => setShops(r.data.shops)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { setQuery(search); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await superAdminApi.get('/insights/activity', {
        params: { shopId: shopId || undefined, action: action || undefined, from: from || undefined, to: to || undefined, search: query || undefined, page, limit: 30 },
      });
      setRows(r.data.activity); setTotal(r.data.total); setPages(r.data.pages);
    } catch { toast.error('Failed to load activity'); }
    setLoading(false);
  }, [shopId, action, from, to, query, page]);
  useEffect(() => { load(); }, [load]);

  const reset = (fn) => (e) => { fn(e.target.value); setPage(1); };

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-control" style={{ width: 200 }} value={shopId} onChange={reset(setShopId)}>
            <option value="">All Shops</option>
            {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <select className="form-control" style={{ width: 190 }} value={action} onChange={reset(setAction)}>
            <option value="">All actions</option>
            {Object.entries(ACTION_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <input type="date" className="form-control" style={{ width: 150 }} value={from} onChange={reset(setFrom)} />
          <span style={{ color: '#94a3b8' }}>to</span>
          <input type="date" className="form-control" style={{ width: 150 }} value={to} onChange={reset(setTo)} />
          <div style={{ position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 10, top: 12, color: '#94a3b8' }} />
            <input className="form-control" style={{ width: 240, paddingLeft: 30 }} placeholder="Search person, customer, bill…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span style={{ color: '#64748b', fontSize: 13 }}>{total} records</span></div>
        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Details</th><th>Shop</th><th>Device / IP</th></tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6}><div className="empty-state"><h3>No activity recorded yet</h3><p style={{ color: '#94a3b8' }}>Every login, order, bill, payment and edit from now on is recorded here.</p></div></td></tr>}
                {rows.map(a => {
                  const m = ACTION_META[a.action] || { label: a.action, color: '#64748b' };
                  return (
                    <tr key={a._id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.createdAt)}</td>
                      <td>{a.userName || '—'}{a.userRole && <div style={{ fontSize: 11, color: '#94a3b8' }}>{roleLabel(a.userRole)}</div>}</td>
                      <td><span style={{ background: `${m.color}1a`, color: m.color, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>{m.label}</span></td>
                      <td style={{ maxWidth: 460 }}>
                        <div>{a.summary}</div>
                        {a.customerId && <span style={{ fontSize: 12, color: '#3b82f6', cursor: 'pointer' }} onClick={() => navigate(`/superadmin/customers/${a.customerId}`)}>Open customer report →</span>}
                        {a.changes && a.changes.length > 0 && (
                          <div>
                            <span style={{ fontSize: 12, color: '#f59e0b', cursor: 'pointer' }} onClick={() => setOpen(open === a._id ? null : a._id)}>
                              {open === a._id ? 'Hide' : 'Show'} what changed ({a.changes.length})
                            </span>
                            {open === a._id && <ChangeList changes={a.changes} />}
                          </div>
                        )}
                      </td>
                      <td>{a.shop?.name || '—'}</td>
                      <td style={{ fontSize: 11, color: '#94a3b8', maxWidth: 200 }}>{a.ip || '—'}<div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.userAgent}>{a.userAgent || ''}</div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div style={{ padding: '12px 16px' }}>
            <div className="pagination">
              {Array.from({ length: Math.min(pages, 15) }, (_, i) => (
                <button key={i} className={`page-btn ${page === i + 1 ? 'active' : ''}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
